<script setup lang="ts">
import IconYes from '@rendora/design/assets/check.svg?component';
import {
  Text,
  type TextColor,
  type Variant,
  type Weight,
} from '@rendora/design/components/text/index';

const props = withDefaults(
  defineProps<{
    checked: boolean;
    label?: string;
    indeterminate?: boolean;
    disabled?: boolean;
    textVariant?: Variant;
    textColor?: TextColor;
    textWeight?: Weight;
    onChange?: (e: boolean) => void;
  }>(),
  {
    textVariant: '14',
    textColor: 't3',
    textWeight: 'regular',
  },
);
const emit = defineEmits(['update:checked']);

function handleChange(e: Event) {
  emit('update:checked', (e.target as HTMLInputElement).checked);
  props.onChange?.((e.target as HTMLInputElement).checked);
}
</script>

<template>
  <label :class="['x-checkbox']">
    <input
      type="checkbox"
      :checked="checked"
      :disabled="disabled"
      @change="handleChange"
      class="x-checkbox__input"
    />
    <IconYes v-if="checked" class="x-checkbox__icon x-checkbox__icon--checked" />
    <div v-else-if="indeterminate" class="x-checkbox__icon x-checkbox__icon--indeterminate" />
    <div v-else-if="disabled" class="x-checkbox__icon x-checkbox__icon--disabled" />
    <div v-else class="x-checkbox__icon x-checkbox__icon--unchecked" />
    <Text
      class="x-checkbox__label"
      v-if="$slots.default || label"
      :variant="textVariant"
      :color="textColor"
      :weight="textWeight"
    >
      <slot v-if="$slots.default"> </slot>
      <template v-else>
        {{ label }}
      </template>
    </Text>
  </label>
</template>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;

.x-checkbox {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;

  @include mobile {
    gap: responsive-size(4px);
  }
}

.x-checkbox__input {
  display: none;
}

.x-checkbox__label {
  flex: 1;
}

.x-checkbox__icon {
  width: 16px;
  height: 16px;
  border-radius: var(--x-radius-small);
  border: 1px solid transparent;
  color: var(--x-white-1);
  cursor: pointer;
  box-sizing: border-box;

  @include mobile {
    width: responsive-size(16px);
    height: responsive-size(16px);
  }
}

.x-checkbox__icon {
  &:has(+ .x-text--12) {
    margin-top: calc((var(--x-text-12-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-12-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--12.x-text--multi) {
    margin-top: calc((var(--x-text-12-multi-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-12-multi-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--14) {
    margin-top: calc((var(--x-text-14-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-14-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--14.x-text--multi) {
    margin-top: calc((var(--x-text-14-multi-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-14-multi-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--16) {
    margin-top: calc((var(--x-text-16-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-16-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--16.x-text--multi) {
    margin-top: calc((var(--x-text-16-multi-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-16-multi-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--18) {
    margin-top: calc((var(--x-text-18-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-18-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--18.x-text--multi) {
    margin-top: calc((var(--x-text-18-multi-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-18-multi-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--20) {
    margin-top: calc((var(--x-text-20-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-20-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--20.x-text--multi) {
    margin-top: calc((var(--x-text-20-multi-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-20-multi-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--24) {
    margin-top: calc((var(--x-text-24-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-24-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--24.x-text--multi) {
    margin-top: calc((var(--x-text-24-multi-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-24-multi-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--28) {
    margin-top: calc((var(--x-text-28-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-28-line-height) - responsive-size(16px)) / 2);
    }
  }
  &:has(+ .x-text--28.x-text--multi) {
    margin-top: calc((var(--x-text-28-multi-line-height) - 16px) / 2);

    @include mobile {
      margin-top: calc((var(--x-text-28-multi-line-height) - responsive-size(16px)) / 2);
    }
  }
}

.x-checkbox__icon--checked {
  background-color: var(--x-purple-6);
  border: none;
}

.x-checkbox__icon--indeterminate {
  background-color: var(--x-purple-6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.x-checkbox__icon--indeterminate::before {
  display: block;
  content: ' ';
  width: 7px;
  height: 1px;
  background-color: var(--x-back-1);
}

.x-checkbox__icon--disabled {
  background-color: var(--x-back-3);
  border: 1px solid var(--x-back-5);
  cursor: not-allowed;
}

.x-checkbox__icon--unchecked {
  background-color: var(--x-back-1);
  border-color: var(--x-back-4);
}

.x-checkbox__icon--unchecked:hover {
  border-color: var(--x-purple-6);
}
</style>
